import { type components } from "@njbsoft/types";

type Document = components["schemas"]["Document"];
type DocumentSource = components["schemas"]["DocumentSource"];

/**
 * Source-aware routing for a repository Document. The repository projects
 * heterogeneous sources into one shape; this module is the single place that
 * maps a Document back to its owning source for:
 *   - the authenticated binary download (which API path streams the blob),
 *   - the source-aware delete (which DELETE endpoint owns the row),
 *   - the "Open [source]" jump-to-source client route (via source_route),
 *   - the protected-source rule (CROMERR, sent notices and generated report
 *     forms are never deletable).
 *
 * Per the backend contract there is intentionally NO delete endpoint for
 * notices, CROMERR custody documents or report form outputs (regulatory
 * copy-of-record), and no generic blob endpoint reachable from the Document
 * fields for CROMERR (its binary lives behind the source's own owner route).
 */

/** True when the source is a protected regulatory record (never deletable). */
export function isProtected(doc: Pick<Document, "source" | "protected">): boolean {
  return (
    doc.protected ||
    doc.source === "cromerr" ||
    doc.source === "notice" ||
    doc.source === "report_form_output"
  );
}

/** True when the binary can be fetched + rendered inline from Document fields. */
export function canInlineDownload(source: DocumentSource): boolean {
  return (
    source === "document" ||
    source === "form" ||
    source === "task" ||
    source === "notice" ||
    source === "report_form_output"
  );
}

/**
 * The authenticated API download path for a Document's binary, or null when the
 * binary is not reachable from the Document fields (cromerr — jump to the
 * source instead). The returned path is consumed by the blob hook (parseAs
 * "blob"), NEVER as a raw `<a href>` (the API requires a Bearer token).
 */
export function downloadPathFor(doc: Document): string | null {
  switch (doc.source) {
    case "document":
      return `/api/v1/orgs/{orgId}/entity-attachments/${doc.id}/download`;
    case "form":
      return `/api/v1/orgs/{orgId}/form-submissions/attachments/${doc.id}/download`;
    case "task":
      // task source: id = task_attachment id, context_id = task id.
      return doc.context_id
        ? `/api/v1/orgs/{orgId}/tasks/${doc.context_id}/attachments/${doc.id}/download`
        : null;
    case "notice":
      return `/api/v1/orgs/{orgId}/notices/${doc.id}/pdf`;
    case "report_form_output":
      return `/api/v1/orgs/{orgId}/report-form-outputs/${doc.id}/pdf`;
    case "cromerr":
      return null;
    default:
      return null;
  }
}

/**
 * The in-app client route to jump to the source record that owns this document
 * (the "Attached to ↗ / Open [source]" link). Derived from source_route + the
 * context/source ids. Returns null when no meaningful in-app target exists.
 */
export function sourceJumpRoute(doc: Document): string | null {
  switch (doc.source_route) {
    case "document":
      // Generic upload — jump to the entity it is attached to, via the finder
      // scoped to that node (the entity detail page mounts the card).
      return doc.context_type && doc.context_id
        ? `/documents?scope=${doc.context_type}:${doc.context_id}`
        : null;
    case "form_submission":
    case "report_form_output":
      // id/source_id is the ATTACHMENT (or report form output) id — the parent
      // submission id rides form_submission_id (projected on document_index).
      // Falls back to the Test Activity page's Submissions tab when the parent
      // is unresolved.
      return doc.form_submission_id
        ? `/test-activity/submissions/${doc.form_submission_id}`
        : `/test-activity?tab=submissions`;
    case "task":
      return doc.context_id ? `/tasks/${doc.context_id}` : `/tasks`;
    case "notice":
      return `/notices/${doc.id}`;
    case "sampling_event":
      // CROMERR custody lives on a sample result; jump to the facility scope.
      return doc.facility_id ? `/documents?scope=facility:${doc.facility_id}` : null;
    default:
      return null;
  }
}
